// A refused move's one seam (#6773, epic #6552), lifted out of `move-page.ts`'s `moveDoors`: a
// send's refusal or failure is announced, a 429 is kept as its item's inline wait, and EVERY
// refusal (a send's, or a drag's at its source or on a drop target) is handed to the page's
// refusal hook with the server's refusal OBJECT and the start stamp of the send or drag, never a
// translated sentence. The transfer stack (`./transfer-stack.ts`) makes its cards from it.

import { t } from '../i18n';
import { MOVE_WAIT_REASONS, type MoveRefusalWire } from './move-calls';
import type { MoveSubject } from './move-items';
import type { MoveOutcome, MovePageDeps, MoveRequest, MoveState, MoveWords } from './move-page';

/**
 * A refused move, handed to the refusal hook (#6773): a send's refusal or failure, or a drag
 * refused at its source or on a drop target. `refusal` is the server's OBJECT (null for a 404,
 * a 5xx or the network), never a translated sentence; `order` is when the send or the drag
 * started (`orderStamp`).
 */
export type MoveRefused = {
  readonly sourceId: string;
  /** Null for a drag refused at its source: it never had a target. */
  readonly targetId: string | null;
  readonly subject?: MoveSubject;
  /** What moves, as the announcement names it. */
  readonly what: string;
  /** An Undo or a Put-back part. */
  readonly putBack: boolean;
  readonly refusal: MoveRefusalWire | null;
  readonly order: number;
};

/** A 429's wait: until `free_at`, at least 1 s (as `moveRetryAfterSeconds`). */
function waitUntil(r: MoveRefusalWire, at: number): number {
  const free = r.free_at == null ? Number.NaN : Date.parse(r.free_at);
  return Number.isNaN(free) ? at + 1_000 : Math.max(free, at + 1_000);
}

/** Hand a refusal to the hook; its own error is reported on its own, never the move's. */
export function notifyRefused(
  deps: MovePageDeps,
  report: (err: unknown) => void,
  r: MoveRefused,
): void {
  try {
    deps.onMoveRefused?.(r);
  } catch (err) {
    report(err);
  }
}

/**
 * A send's refusal or failure (lifted out of `moveDoors`): announced, a 429 kept as the item's
 * inline wait, and handed to the refusal hook with the send's start stamp.
 */
export function refuseSend(
  st: MoveState,
  deps: MovePageDeps,
  w: MoveWords,
  at: { readonly read: number; readonly order: number; readonly report: (e: unknown) => void },
  req: MoveRequest,
  refusal: MoveRefusalWire | null,
): MoveOutcome {
  const { subject } = req;
  notifyRefused(deps, at.report, {
    sourceId: req.sourceId,
    targetId: req.targetId,
    ...(subject === undefined ? {} : { subject }),
    what: req.what,
    putBack: req.undoOf !== undefined || req.putBack === true,
    refusal,
    order: at.order,
  });
  if (refusal === null) {
    w.announce(t('fleet.move.move_failed'));
    return { kind: 'failed' };
  }
  // The sentence counts from NOW: the clock may not have ticked since the preview landed.
  st.retick();
  const text = w.textOf(refusal);
  w.announce(text);
  if (!MOVE_WAIT_REASONS.has(refusal.reason)) return { kind: 'refused', refusal, text };
  st.putWait(req.key, {
    refusal,
    sourceId: req.sourceId,
    items: req.items,
    read: at.read,
    targetId: req.targetId,
    until: waitUntil(refusal, Date.now()),
  });
  return { kind: 'wait', refusal, text };
}
